import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Phone } from 'lucide-react';
import { authAPI } from '../services/api';

const Login: React.FC = () => {
    const [phoneNumber, setPhoneNumber] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const navigate = useNavigate();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const response = await authAPI.requestOTP(phoneNumber);

            // Store phone number for OTP verification
            localStorage.setItem('temp_phone', phoneNumber);

            // Navigate to OTP verification page
            navigate('/verify-otp', {
                state: {
                    phoneNumber,
                    message: response.message
                }
            });
        } catch (err: unknown) {
            const errorMessage = err instanceof Error && 'response' in err
                ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
                : 'ارسال کد تایید با خطا مواجه شد. لطفا دوباره تلاش کنید.';
            setError(errorMessage || 'ارسال کد تایید با خطا مواجه شد. لطفا دوباره تلاش کنید.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-100 via-emerald-50 to-teal-100 p-4" dir="rtl">
            <Card className="w-full max-w-md shadow-2xl border-green-200">
                <CardHeader className="space-y-1 text-center bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-t-lg">
                    <CardTitle className="text-2xl font-bold">پنل نمایندگان</CardTitle>
                    <CardDescription className="text-green-100">
                        شماره تلفن خود را برای دریافت کد تایید وارد کنید
                    </CardDescription>
                </CardHeader>
                <CardContent className="mt-6">
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="phone" className="text-green-800">شماره تلفن</Label>
                            <div className="relative">
                                <Phone className="absolute right-3 top-3 h-4 w-4 text-green-600" />
                                <Input
                                    id="phone"
                                    type="tel"
                                    placeholder="09901152559"
                                    value={phoneNumber}
                                    onChange={(e) => setPhoneNumber(e.target.value)}
                                    className="pr-10 border-green-300 focus:ring-green-500 focus:border-green-500"
                                    required
                                />
                            </div>
                        </div>

                        {error && (
                            <Alert variant="destructive" className="border-red-300 bg-red-50">
                                <AlertDescription className="text-red-700">{error}</AlertDescription>
                            </Alert>
                        )}

                        <Button
                            type="submit"
                            className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white"
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="ml-2 h-4 w-4 animate-spin" />
                                    در حال ارسال کد...
                                </>
                            ) : (
                                'ارسال کد تایید'
                            )}
                        </Button>
                    </form>

                    <div className="mt-6 text-center">
                        <p className="text-sm text-green-700">
                            حساب کاربری ندارید؟{' '}
                            <Link to="/request-agency" className="text-green-600 hover:text-green-800 font-medium hover:underline">
                                درخواست نمایندگی
                            </Link>
                        </p>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export default Login;
